// App.js
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ChangePassword from "./pages/ChangePassword";
import Dashboard from "./pages/Dashboard";
import DispatcherBoard from "./pages/DispatcherBoard";
import DispatcherLayout from "./pages/dispatcher/DispatcherLayout";
import DashboardHome from "./pages/dispatcher/DashboardHome";
import LoadsPage from "./pages/dispatcher/LoadsPage";
import TrucksTrailersPage from "./pages/dispatcher/TrucksTrailersPage";
import DriversPage from "./pages/dispatcher/DriversPage";
import Alerts from "./pages/dispatcher/Alerts";
import Integrations from "./pages/dispatcher/Integrations";
import FinanceReports from "./pages/dispatcher/FinanceReports";
import SettingsLink from "./pages/dispatcher/SettingsLink";
import FleetCostSettings from "./pages/dispatcher/FleetCostSettings";
import InfraCostReport from "./pages/dispatcher/InfraCostReport";
import FleetOperationalSettings from "./pages/dispatcher/FleetOperationalSettings";
import Onboarding from "./pages/dispatcher/Onboarding";
import LoadBoard from "./pages/dispatcher/LoadBoard";
import DispatcherPerformance from "./pages/dispatcher/DispatcherPerformance";
import TruckCostReport from "./pages/dispatcher/TruckCostReport";
import FuelSavingsOpportunity from "./pages/FuelSavingsOpportunity";
import FinanceDashboard from "./pages/FinanceDashboard";
import RouteHistory from "./pages/dispatcher/RouteHistory";
import IdleMiles from "./pages/dispatcher/IdleMiles";
import TruckHistoryMap from "./pages/dispatcher/TruckHistoryMap";
import EnRoutePickup from "./pages/dispatcher/EnRoutePickup";
import HosCompliance from "./pages/dispatcher/HosCompliance";
import LaneProfitability from "./pages/dispatcher/LaneProfitability";
import FuelCompliance from "./pages/dispatcher/FuelCompliance";
import TollCompliance from "./pages/dispatcher/TollCompliance";
import EtaDriftHistory from "./pages/dispatcher/EtaDriftHistory";
import TeamMembers from "./pages/dispatcher/TeamMembers";
import Announcements from "./pages/dispatcher/Announcements";
import FleetAssistant from "./pages/dispatcher/FleetAssistant";
import AIUsageDashboard from "./pages/AIUsageDashboard";
import CommandCenter from "./pages/CommandCenter";
import PortalView from "./pages/PortalView";
import PrivateRoute from "./components/PrivateRoute";
import Unauthorized from "./pages/Unauthorized";
import { Toaster } from "react-hot-toast";
import './index.css';
import "react-datepicker/dist/react-datepicker.css";
import './styles/global.css';

// AI Architect Lab lives inside the Dashboard shell (/dashboard/ai-architect-lab)
// so the left Navigator + header stay visible -- this route exists only so old
// bookmarks/links to the standalone path still land somewhere useful.
const RedirectWithSessionId = ({ to }) => {
  const { sessionId } = useParams();
  return <Navigate to={sessionId ? `${to}/${sessionId}` : to} replace />;
};

const App = () => {
  return (
    <>
      <Toaster />

      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/unauthorized" element={<Unauthorized />} />
        {/* Reachable only via PrivateRoute's own mustResetPassword redirect
            (see components/PrivateRoute.js) -- still wrapped in PrivateRoute
            itself (no requireRoles) so it requires a real logged-in session,
            not a public route. */}
        <Route
          path="/change-password"
          element={
            <PrivateRoute>
              <ChangePassword />
            </PrivateRoute>
          }
        />

        {/* This app is fleet dispatch operations, not the generic Table
            Builder / AI Architect shell -- only super_admin (whose job
            includes building/administering the platform itself) can reach
            it. dispatcher/admin land straight on /dispatch instead of a
            dead-end Unauthorized page, matching login_redirect_rules'
            post-login behavior for those roles (see migration 0042). */}
        <Route
          path="/dashboard/*"
          element={
            <PrivateRoute requireRoles={["super_admin"]} fallback="/dispatch">
              <Dashboard />
            </PrivateRoute>
          }
        />

        <Route
          path="/ai-architect-lab/:sessionId?"
          element={<RedirectWithSessionId to="/dashboard/ai-architect-lab" />}
        />

        {/* layout_mode=standalone pages -- own branded layout, no admin
            sidebar, outside PrivateRoute since requires_auth is a per-page
            setting (checked server-side by GET /pages/:slug/bootstrap). */}
        <Route path="/portal/:slug" element={<PortalView />} />

        {/* The full-featured board (broker-call matching, negotiation,
            route comparison, live HOS/map) that Phase 2 will port into the
            new shell below piece by piece -- kept reachable directly so
            none of that functionality is lost in the meantime. Declared
            BEFORE the /dispatch/* shell below so this specific path is
            never swallowed by that wildcard's own nested routing. */}
        <Route
          path="/dispatch/operational-center"
          element={
            <PrivateRoute requireRoles={["dispatcher", "admin", "super_admin"]} fallback="/unauthorized">
              <DispatcherBoard />
            </PrivateRoute>
          }
        />

        {/* New dispatcher shell (sidebar + light-theme pages), replacing the
            old standalone /dispatch board. Phase 1: Dashboard/Loads/Trucks &
            Trailers/Drivers are real; everything else (including Routes) is
            "Coming soon" until Phase 2 ports the matching/negotiation/route-
            comparison flow out of DispatcherBoard.jsx into this shell. */}
        <Route
          path="/dispatch/*"
          element={
            <PrivateRoute fallback="/unauthorized">
              <DispatcherLayout />
            </PrivateRoute>
          }
        >
          <Route index element={<DashboardHome />} />
          <Route path="loads" element={<LoadsPage />} />
          <Route path="trucks" element={<TrucksTrailersPage />} />
          <Route path="drivers" element={<DriversPage />} />
          {/* Admin/super_admin only -- a dispatcher has no use for fleet-wide
              finance/cost reports, only their own trucks' operational data
              (covered by the pages above). */}
          <Route
            path="reports"
            element={
              <PrivateRoute requireRoles={["admin", "super_admin"]} fallback="/unauthorized">
                <FinanceReports />
              </PrivateRoute>
            }
          />
          {/* Read-only Fleet-Admin Assistant (Claude Sonnet) -- answers any
              fleet question with sources, gated server-side to admin/super_admin. */}
          <Route
            path="assistant"
            element={
              <PrivateRoute requireRoles={["admin", "super_admin"]} fallback="/unauthorized">
                <FleetAssistant />
              </PrivateRoute>
            }
          />
          <Route path="alerts" element={<Alerts />} />
          {/* Weekly leaderboard -- open to dispatcher/admin/super_admin, not
              admin-only like the reports below. It's a competition board by
              design: every dispatcher sees every dispatcher's numbers,
              matching the backend scripted API's own (now widened) role
              check. */}
          <Route
            path="performance"
            element={
              <PrivateRoute requireRoles={["dispatcher", "admin", "super_admin"]} fallback="/unauthorized">
                <DispatcherPerformance />
              </PrivateRoute>
            }
          />
          {/* Dispatcher -> truck -> trip cost/profit breakdown + weekly
              truck-cost-coverage status -- admin/super_admin only (same
              gate as Finance/Fleet Cost Settings), matching the backend
              scripted API's own role check. */}
          <Route
            path="truck-economics"
            element={
              <PrivateRoute requireRoles={["admin", "super_admin"]} fallback="/unauthorized">
                <TruckCostReport />
              </PrivateRoute>
            }
          />
          {/* Consolidated Finance dashboard -- one tabbed module (Overview + deep-dive tabs)
              folding the scattered finance reports together; admin/super_admin only, read-only. */}
          <Route
            path="finance"
            element={
              <PrivateRoute requireRoles={["admin", "super_admin"]} fallback="/unauthorized">
                <FinanceDashboard />
              </PrivateRoute>
            }
          />
          {/* Fuel Savings / Value Intelligence -- admin/super_admin (fleet admin) only; read-only.
              Still reachable directly; also folded in as the Finance > Fuel tab. */}
          <Route
            path="fuel-savings"
            element={
              <PrivateRoute requireRoles={["admin", "super_admin"]} fallback="/unauthorized">
                <FuelSavingsOpportunity />
              </PrivateRoute>
            }
          />
          {/* After-the-fact off-route audit list -- open to dispatchers now,
              scoped server-side to their own trucks (admin/super_admin still
              see everything), matching the backend scripted API's own role
              check + own_truck_ids filter. */}
          <Route
            path="route-history"
            element={
              <PrivateRoute requireRoles={["dispatcher", "admin", "super_admin"]} fallback="/unauthorized">
                <RouteHistory />
              </PrivateRoute>
            }
          />
          {/* Per-truck route-history MAP: one truck's full movement (loaded +
              deadhead + idle) over a window on a single map. Admin/super_admin
              only, matching the backend truck_history router's own gate. */}
          <Route
            path="truck-map"
            element={
              <PrivateRoute requireRoles={["admin", "super_admin"]} fallback="/unauthorized">
                <TruckHistoryMap />
              </PrivateRoute>
            }
          />
          {/* Idle/unassigned-truck miles -- same "own trucks only" scoping
              as Route History above. */}
          <Route
            path="idle-miles"
            element={
              <PrivateRoute requireRoles={["dispatcher", "admin", "super_admin"]} fallback="/unauthorized">
                <IdleMiles />
              </PrivateRoute>
            }
          />
          {/* En Route to Pickup -- live board of trucks deadheading to a booked
              pickup. Distinct from Idle Movement. Own-trucks/all scoping. */}
          <Route
            path="en-route-to-pickup"
            element={
              <PrivateRoute requireRoles={["dispatcher", "admin", "super_admin"]} fallback="/unauthorized">
                <EnRoutePickup />
              </PrivateRoute>
            }
          />
          {/* Real per-driver daily-HOS-limit violation history (driver_hos_daily,
              persisted forever) -- same "own trucks only" scoping as Route
              History/Idle Miles above (resolved driver -> current truck ->
              dispatcher server-side). */}
          <Route
            path="hos-compliance"
            element={
              <PrivateRoute requireRoles={["dispatcher", "admin", "super_admin"]} fallback="/unauthorized">
                <HosCompliance />
              </PrivateRoute>
            }
          />
          {/* Planned-vs-actual fuel stop compliance, grouped by truck (see
              fuel_compliance_by_truck's own docstring for why not driver --
              no historical driver-per-load attribution exists in this
              schema). Same "own trucks only" scoping as the reports above. */}
          <Route
            path="fuel-compliance"
            element={
              <PrivateRoute requireRoles={["dispatcher", "admin", "super_admin"]} fallback="/unauthorized">
                <FuelCompliance />
              </PrivateRoute>
            }
          />
          {/* Real toll-pass totals, grouped by truck -- planned/alternate-route/
              live-reroute kept as separate figures (see toll_compliance_by_truck's
              own docstring). Same "own trucks only" scoping as the reports above. */}
          <Route
            path="toll-compliance"
            element={
              <PrivateRoute requireRoles={["dispatcher", "admin", "super_admin"]} fallback="/unauthorized">
                <TollCompliance />
              </PrivateRoute>
            }
          />
          {/* Real revenue/profit grouped by lane (origin -> destination) --
              "own loads only" scoping for a plain dispatcher (loads.dispatcher_id,
              stamped at booking), fleet-wide for admin/super_admin. */}
          <Route
            path="lane-profitability"
            element={
              <PrivateRoute requireRoles={["dispatcher", "admin", "super_admin"]} fallback="/unauthorized">
                <LaneProfitability />
              </PrivateRoute>
            }
          />
          {/* Per-load ETA drift episode history (eta_drift_events, persisted
              forever) -- same "own trucks only" scoping as HOS Compliance/
              Route History/Idle Miles above. */}
          <Route
            path="eta-drift-history"
            element={
              <PrivateRoute requireRoles={["dispatcher", "admin", "super_admin"]} fallback="/unauthorized">
                <EtaDriftHistory />
              </PrivateRoute>
            }
          />
          <Route path="integrations" element={<Integrations />} />
          <Route path="settings" element={<SettingsLink />} />
          {/* Add/manage team members -- admin/super_admin only. Real
              server-side gating lives in main.py's /users/create-with-roles
              (fleet admins can assign any role except super_admin), not just
              this route guard. */}
          <Route
            path="team"
            element={
              <PrivateRoute requireRoles={["admin", "super_admin"]} fallback="/unauthorized">
                <TeamMembers />
              </PrivateRoute>
            }
          />
          {/* Publishes the sticky banner every dispatcher sees on Dispatch
              home + Operational Center (see AnnouncementBanner.jsx) --
              admin/super_admin only, matching migration 0071's own ACL. */}
          <Route
            path="announcements"
            element={
              <PrivateRoute requireRoles={["admin", "super_admin"]} fallback="/unauthorized">
                <Announcements />
              </PrivateRoute>
            }
          />
          {/* Real editable fleet cost inputs (insurance, overhead, maintenance,
              equipment, wait-time) -- admin/super_admin only, and unlike most
              other dispatcher-shell pages this one WRITES data, so it gets a
              real route guard (not just a hidden nav link) on top of the
              backend's own ACL check. */}
          <Route
            path="fleet-cost-settings"
            element={
              <PrivateRoute requireRoles={["admin", "super_admin"]} fallback="/unauthorized">
                <FleetCostSettings />
              </PrivateRoute>
            }
          />
          {/* Infrastructure & services cost dashboard (EUR) -- AWS live via Cost
              Explorer + manual external line items, from the daily snapshot.
              Read-only report, admin/super_admin only. */}
          <Route
            path="infra-cost-report"
            element={
              <PrivateRoute requireRoles={["admin", "super_admin"]} fallback="/unauthorized">
                <InfraCostReport />
              </PrivateRoute>
            }
          />
          {/* Non-cost operational knobs split out of the above (alert
              thresholds, matching radius, feature toggles, fuel-stop
              optimization) -- same write-access route guard. */}
          <Route
            path="fleet-operational-settings"
            element={
              <PrivateRoute requireRoles={["admin", "super_admin"]} fallback="/unauthorized">
                <FleetOperationalSettings />
              </PrivateRoute>
            }
          />
          {/* Guided, revisitable walk-through of the same fleet.* properties
              above -- writes through the same rows, same route guard. */}
          <Route
            path="onboarding"
            element={
              <PrivateRoute requireRoles={["admin", "super_admin"]} fallback="/unauthorized">
                <Onboarding />
              </PrivateRoute>
            }
          />
          {/* One shared weekly board (replaces per-dispatcher spreadsheets) --
              same audience as the Operational Center itself, not an
              admin-only settings page. */}
          <Route
            path="load-board"
            element={
              <PrivateRoute requireRoles={["dispatcher", "admin", "super_admin"]} fallback="/unauthorized">
                <LoadBoard />
              </PrivateRoute>
            }
          />
          {/* super_admin-only: real per-provider (HERE + Claude) API spend,
              sourced from ai_usage_logs -- see backend/routers/scripted_runtime.py's
              _log_here_usage and ai_agents/runtime.py's _log_usage. */}
          <Route path="api-usage" element={<AIUsageDashboard />} />
          <Route path="command-center" element={<CommandCenter embedded />} />
        </Route>

        <Route path="*" element={<Login />} />
      </Routes>
    </>
  );
};

export default App;
